import React, { useMemo } from 'react';
import { styled, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { demographicSources } from '@lib/constants';
import EventDemographicsModal from '@components/ContentComponents/Events/Demographics/EventDemographicsModal';
import { getAgeGroups, getMaxSessionParticipants } from '@components/Pages/Events/eventFunctions';
import useSessionParticipantsQuery from "@components/common/hooks/api/useSessionParticipantsQuery";
import useDemographicOptionsQuery from "@components/common/hooks/api/useDemographicOptionsQuery";

const DataHeading = styled(Typography)({
  fontSize: '16px',
  lineHeight: '40px',
});

const DataTable = styled(Table)({
  width: 'auto',
  marginBottom: '24px',
});

const TableHeadCell = styled(TableCell)({
  backgroundColor: '#f4f4f4',
  fontWeight: 'bold',
  height: '56px',
  padding: '4px 8px',
  textAlign: 'center',
});

const TableDataCell = styled(TableCell)({
  height: '56px',
  textAlign: 'center',
});

const EventDemographics = ({ event, showEdit }) => {
  const theme = useTheme();

  const { data: participants, ...participantsQuery } = useSessionParticipantsQuery(event.id);
  const { data: demographicOptions, ...demographicOptionsQuery } = useDemographicOptionsQuery();
  const ageRanges = getAgeGroups(event, demographicOptions);

  let ageRangeRows = [];
  if (event.curriculum?.audience === 'Family') {
    // Family: split the age range fields onto two rows. "No answer" and "unknown" go in the Adult row.
    ageRangeRows = [
      ageRanges.filter(r => r.audience === 'YOUTH'),
      ageRanges.filter(r => r.audience !== 'YOUTH')
    ];
  } else {
    // Adults/Youth: show all fields in one row.
    ageRangeRows.push(ageRanges);
  }

  const uniqueParticipants = useMemo(() => {
    if (!event) {
      return '';
    }
    return getMaxSessionParticipants(event);
  }, [event, participants]);

  return (
    <>
      {showEdit && (
        <EventDemographicsModal event={event} />
      )}

      <Typography variant="body1" sx={{ fontFamily: 'Montserrat', marginTop: '38px', marginBottom: '24px' }} gutterBottom>
          <span style={{ fontWeight: 600, fontSize: theme.typography.pxToRem(22) }}>
            Total Participants for this event: {uniqueParticipants || '-'}
          </span>
      </Typography>

      {event?.demographics && (
        <>
          <DataHeading variant="h4">AGE GROUP</DataHeading>

          {/* FIXME: some values aren't appearing here. */}
          {ageRangeRows.map((ranges, idx) => (
            <DataTable key={idx} data-cy="table-age">
              <TableHead>
                <TableRow>
                  {ranges.map(ageRange => (
                    <TableHeadCell key={`th-${ageRange.code}`} sx={{ 'width': `${100 / ageRanges.length}%` }}>
                      {ageRange.label}
                    </TableHeadCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {ranges.map(ageRange => (
                    <TableDataCell key={`td-${ageRange.code}`}>
                      {event.demographics.age[ageRange.code] || 0}
                    </TableDataCell>
                  ))}
                </TableRow>
              </TableBody>
            </DataTable>
          ))}

          <DataHeading variant="h4">GENDER</DataHeading>

          <DataTable data-cy="table-gender">
            <TableHead>
              <TableRow>
                {demographicOptions?.gender?.map(gender => (
                  <TableHeadCell key={`th-${gender.code}`} sx={{ width: '20%' }}>
                    {gender.label}
                  </TableHeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {demographicOptions?.gender?.map((gender) => (
                  <TableDataCell key={`td-${gender.code}`}>
                    {event.demographics.gender[gender.code] || 0}
                  </TableDataCell>
                ))}
              </TableRow>
            </TableBody>
          </DataTable>

          <DataHeading variant="h4">RACE AND ETHNICITY</DataHeading>

          <DataTable data-cy="table-race" sx={{ maxWidth: '1250px' }}>
            <TableHead>
              <TableRow>
                {demographicOptions?.raceEthnicity?.map(race => (
                  <TableHeadCell sx={{
                    maxWidth: 180,
                    width: '12.5%',
                  }} key={`th-${race.code}`}>
                    {race.label}
                  </TableHeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {demographicOptions?.raceEthnicity?.map(race => (
                  <TableDataCell key={`td-${race.code}`}>
                    {event.demographics.raceEthnicity[race.code] || 0}
                  </TableDataCell>
                ))}
              </TableRow>
            </TableBody>
          </DataTable>

          <Box mt="28px">
            <Typography variant="body1" sx={{ fontWeight: 600 }}>Who did you get the demographics from?</Typography>
            <Typography variant="body1" sx={{
              // borderBottom: '1px solid rgba(224, 224, 224, 1)',
              mt: 2,
              mb: 2,
              maxWidth: '150px',
              minHeight: 20,
            }}>
              {demographicSources.find(opt => opt.id === event.demographics?.source)?.label}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};
export default EventDemographics;
